// Cliente HTTP mínimo — mesma semântica do apiBase.ts do app original:
// - token Bearer anexado quando existe sessão;
// - 401 em request autenticado derruba a sessão em um único lugar.
//
// POLÍTICA DE SESSÃO (pedido do produto): fechar a página/app = logoff
// automático. Por isso o token vive em sessionStorage (morre com a aba;
// sobrevive a reload e a backgrounding do PWA). O caminho rápido de volta
// é o login por biometria — cuja flag de opt-in fica em localStorage
// (lib/webauthn.ts) justamente para sobreviver ao fim da sessão.

const TOKEN_KEY = "bolao-pga/auth-token";

// Migração: versões anteriores persistiam o token em localStorage; remove
// qualquer resíduo para que sessões antigas não sobrevivam à nova política.
localStorage.removeItem(TOKEN_KEY);

let onSessionInvalid: (() => void) | null = null;

export function setSessionInvalidHandler(handler: (() => void) | null): void {
  onSessionInvalid = handler;
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  readonly status: number;
  /** corpo da resposta de erro (ex.: { error, needsVerification, email }) */
  readonly data: unknown;
  constructor(status: number, message: string, data: unknown = null) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const { auth = true, ...init } = options;
  const headers = new Headers(init.headers);
  if (!headers.has("content-type") && init.body) {
    headers.set("content-type", "application/json");
  }
  const token = auth ? getToken() : null;
  if (token) headers.set("authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers });

  if (res.status === 401 && token) {
    onSessionInvalid?.();
  }

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Erro ${res.status}`;
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}
